import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { setApiUrl, getApiUrl } from '@/api/client';

export function ConnectionSetupPage() {
  const navigate = useNavigate();
  const [url, setUrl] = useState(getApiUrl() ?? '');
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'ok' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const test = async () => {
    setTesting(true);
    setStatus('idle');
    setError(null);
    try {
      const trimmed = url.trim().replace(/\/+$/, '');
      const probe = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
      const r = await axios.get(`${probe}/api/health`, { timeout: 10_000 });
      if (r.status >= 200 && r.status < 300) {
        setStatus('ok');
        setApiUrl(probe);
      } else {
        setStatus('error');
        setError(`Unexpected status ${r.status}`);
      }
    } catch (err) {
      setStatus('error');
      setError((err as Error).message ?? 'Connection failed');
    } finally {
      setTesting(false);
    }
  };

  const proceed = () => {
    if (status === 'ok') navigate('/login');
  };

  return (
    <div className="pt-4-safe mx-auto max-w-md p-4">
      <Card>
        <CardHeader>
          <CardTitle>Connection setup</CardTitle>
        </CardHeader>
        <CardBody>
          <p className="text-sm text-brand-muted">
            Enter the middleware URL provided by your dispatcher. Example:{' '}
            <code className="text-brand-text">https://api.example.com</code>
          </p>
          <Input
            label="Middleware URL"
            type="url"
            inputMode="url"
            placeholder="https://api.example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          <div className="flex gap-2">
            <Button variant="primary" onClick={test} disabled={!url.trim() || testing} fullWidth>
              {testing ? 'Testing...' : 'Test connection'}
            </Button>
            <Button variant="delivery" onClick={proceed} disabled={status !== 'ok'} fullWidth>
              Continue
            </Button>
          </div>
          {status === 'ok' && <p className="text-sm text-brand-delivery">Connection OK.</p>}
          {status === 'error' && <p className="text-sm text-brand-error">Failed: {error}</p>}
        </CardBody>
      </Card>
    </div>
  );
}
